import type { ReelItem } from './types';

import { getSavedBandwidthEstimate } from '~/composables/useHlsPlayer/shakaLoader';
import {
	getProxiedVideoUrl,
	prefetchFirstSegment
} from '~/composables/useVideoUrls';

import { clearIOSQualityCache, setIOSHighQualityUrl } from './iosQualityCache';

const HIGH_QUALITY_THRESHOLD_BPS = 4_000_000;

// On slow networks, delay prefetch so the current video has time to buffer
// before we consume bandwidth for upcoming videos.
function getPrefetchDelay(): number {
	const bps = getSavedBandwidthEstimate();
	if (bps === null || bps >= HIGH_QUALITY_THRESHOLD_BPS) return 0; // fast / unknown: immediate
	if (bps >= 2_000_000) return 1_000; // medium (~2-4 Mbps): 1s
	return 2_500; // slow (<2 Mbps): 2.5s
}

interface UseShortsPrefetchOptions {
	getItems: () => ReelItem[];
}

export function useShortsPrefetch(options: UseShortsPrefetchOptions) {
	const prefetchedUrls = new Set<string>();
	const inFlightUrls = new Set<string>();
	let prefetchAbortController: AbortController | null = null;
	let prefetchDelayTimer: ReturnType<typeof setTimeout> | null = null;

	function prefetchUpcoming(startIndex: number, count: number = 3) {
		if (import.meta.server) return;

		if (prefetchAbortController) {
			prefetchAbortController.abort();
		}

		if (prefetchDelayTimer) {
			clearTimeout(prefetchDelayTimer);
			prefetchDelayTimer = null;
		}

		prefetchAbortController = new AbortController();
		const { signal } = prefetchAbortController;

		const delay = getPrefetchDelay();
		const run = () => {
			prefetchDelayTimer = null;
			if (signal.aborted) return;
			const items = options.getItems();
			runPrefetch(items, startIndex, count, signal);
		};

		if (delay > 0) prefetchDelayTimer = setTimeout(run, delay);
		else run();
	}

	function runPrefetch(
		items: ReelItem[],
		startIndex: number,
		count: number,
		signal: AbortSignal
	) {
		for (let i = 1; i <= count; i++) {
			const nextIndex = startIndex + i;
			if (nextIndex >= items.length) break;

			const item = items[nextIndex];
			if (!item?.videoUrl) continue;

			const videoUrl = getProxiedVideoUrl(item.videoUrl);
			if (
				!videoUrl ||
				prefetchedUrls.has(videoUrl) ||
				inFlightUrls.has(videoUrl)
			)
				continue;

			prefetchedUrls.add(videoUrl);
			inFlightUrls.add(videoUrl);

			const onSuccess = () => {
				inFlightUrls.delete(videoUrl);
			};
			const onError = (error: Error) => {
				inFlightUrls.delete(videoUrl);
				prefetchedUrls.delete(videoUrl);
			};

			if (i <= 1) {
				fetch(videoUrl, { priority: 'low', signal } as RequestInit)
					.then(onSuccess)
					.catch(onError);
			} else {
				const savedBps = getSavedBandwidthEstimate();
				const networkIsGood =
					savedBps !== null && savedBps >= HIGH_QUALITY_THRESHOLD_BPS;

				if (networkIsGood) {
					prefetchFirstSegment(videoUrl, signal, 'highest')
						.then(variantUrl => {
							if (variantUrl) setIOSHighQualityUrl(videoUrl, variantUrl);
							onSuccess();
						})
						.catch(onError);
				} else {
					// Low quality first guarantees something is ready; high quality is
					// attempted opportunistically — abort/failure leaves low quality intact.
					const lowQuality = prefetchFirstSegment(videoUrl, signal, 'lowest');

					lowQuality.then(onSuccess).catch(onError);

					lowQuality
						.then(() => prefetchFirstSegment(videoUrl, signal, 'highest'))
						.then(variantUrl => {
							if (variantUrl) setIOSHighQualityUrl(videoUrl, variantUrl);
						})
						.catch(() => {});
				}
			}
		}
	}

	function cleanupPrefetch() {
		if (prefetchDelayTimer) {
			clearTimeout(prefetchDelayTimer);
			prefetchDelayTimer = null;
		}
		prefetchAbortController?.abort();
		prefetchAbortController = null;
		prefetchedUrls.clear();
		inFlightUrls.clear();
		clearIOSQualityCache();
	}

	function abortCurrentPrefetch() {
		if (prefetchDelayTimer) {
			clearTimeout(prefetchDelayTimer);
			prefetchDelayTimer = null;
		}
		prefetchAbortController?.abort();
		prefetchAbortController = null;
	}

	return {
		prefetchUpcoming,
		abortCurrentPrefetch,
		cleanupPrefetch
	};
}
